import React, { useState, useEffect, useRef } from 'react'
import confetti from 'canvas-confetti'
import {
  AppDialog,
  AppDialogContent,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogDescription,
  AppDialogBody,
  AppDialogFooter,
  AppButton,
  useToastV2,
} from '@easyllama/ui-components'
import { useApp } from '~/context/appContext'
import ContactSalesDialog from '~/molecules/Billing/ContactSalesDialog'
import AddPaymentMethodDialog from '~/molecules/Billing/AddPaymentMethodDialog'
import { getSeatRules } from '~/service/Billing/billingConstants'
import { fetchPlans, updatePrimaryPaymentMethod, createSubscription, updateSubscription } from '~/service/Billing/fetchApiClient'
import { fetchQuickbooksCallbackUrl } from '~/service/Quickbooks2/fetchApiClient'
import { closeQuickbooksIframe } from '~/service/Quickbooks2/closeQuickbooksIframe'
import ChangePlanSuccessStep from '~/molecules/Billing/ChangePlanSuccessStep'
import ChangePlanFormPanel from '~/molecules/Billing/ChangePlanFormPanel'
import ChangePlanSummaryPanel from '~/molecules/Billing/ChangePlanSummaryPanel'
import ChangePlanTerms from '~/molecules/Billing/ChangePlanTerms'
import { courseAuthoringTrainingsPath } from '~/routes'
import { UPGRADE_SOURCE_COURSE_AUTHORING, REOPEN_CREATE_COURSE_PARAM } from '~/utils/upgradeUrl'

const PLAN_TIER = { pay_per_course: 0, core: 1, ultimate: 2 }

export default function ChangePlanDialog({ open, onOpenChange, plans = [], initialPlan, initialCount, initialEstimate, variant = 'dialog', showContactSales = true, pendingInlineSubscription = null, onInlineSubscriptionChange, source = null, restrictUnit = null, onSuccess = null, onViewDetails = null }) {
  // announcementRenderToken is set only by useAnnouncementBilling, so an upgrade started
  // from the billing page itself attributes to nothing.
  const { company, jwt, csrf, subscription, paymentMethods, setPaymentMethods, refreshPaymentMethods, refreshSubscription, announcementRenderToken } = useApp()
  const toast = useToastV2()
  const { minCount } = getSeatRules(company)

  const [selectedUnit, setSelectedUnit] = useState(initialPlan?.id ?? '')
  const [seats, setSeats] = useState(initialCount ?? minCount)
  const [selectedPaymentId, setSelectedPaymentId] = useState('')
  const [estimate, setEstimate] = useState(initialEstimate)
  const [estimateLoading, setEstimateLoading] = useState(false)
  const [salesDialogMode, setSalesDialogMode] = useState(null) // 'contact' | null
  const [addPaymentOpen, setAddPaymentOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [successPlanName, setSuccessPlanName] = useState('')
  const [successIsUpgrade, setSuccessIsUpgrade] = useState(true)

  const isNewOrCanceledSubscription = !subscription || subscription.status === 'canceled'
  const totalSeats = isNewOrCanceledSubscription ? seats : (subscription.licensesCount || 0) + seats

  const [latestPlans, setLatestPlans] = useState(plans)

  const debounceRef = useRef(null)
  const isFirstRender = useRef(true)
  const selectedUnitRef = useRef(null)
  const hasFetchedRef = useRef(false)
  // Quantity the currently displayed estimate (and `latestPlans`) was priced for.
  const estimatedQuantityRef = useRef(null)

  useEffect(() => {
    if (!hasFetchedRef.current) setLatestPlans(plans)
  }, [plans])

  useEffect(() => {
    if (!open) {
      isFirstRender.current = true
      selectedUnitRef.current = null
      hasFetchedRef.current = false
      estimatedQuantityRef.current = null
      setSalesDialogMode(null)
      setAddPaymentOpen(false)
      setEstimateLoading(false)
      return
    }
    setIsSuccess(false)
    setSuccessPlanName('')
    setSelectedUnit(initialPlan?.id ?? '')
    setSeats(initialCount ?? minCount)
    setEstimate(initialEstimate)
    isFirstRender.current = false
  }, [open])

  useEffect(() => {
    if (!open) return
    const primary = paymentMethods?.find((m) => m.primary)
    if (primary) setSelectedPaymentId(primary.id?.toString() ?? '')
  }, [open, paymentMethods])

  useEffect(() => {
    if (!isSuccess) return
    confetti({ particleCount: 120, spread: 80, origin: { y: 0.55 } })
  }, [isSuccess])

  // When the selected plan changes, read the estimate from the latest fetched plans — no API call.
  useEffect(() => {
    if (!open || !selectedUnit) return
    if (isFirstRender.current) return
    selectedUnitRef.current = selectedUnit
    const plan = latestPlans.find((p) => p.unit === selectedUnit)
    setEstimate(plan?.estimate ?? null)
  }, [selectedUnit])

  // Keyed on the quantity, not on `seats` changing: the dialog re-opens on a count it may already hold.
  useEffect(() => {
    if (!open || !selectedUnit || seats == null) return
    // Already priced: skip the call, and clear loading in case a scheduled fetch was cancelled.
    if (estimatedQuantityRef.current === totalSeats) {
      setEstimateLoading(false)
      return
    }
    selectedUnitRef.current = selectedUnit
    const controller = new AbortController()
    clearTimeout(debounceRef.current)
    setEstimateLoading(true)
    debounceRef.current = setTimeout(() => {
      fetchPlans(company.id, jwt, totalSeats, controller.signal)
        .then((plansData) => {
          hasFetchedRef.current = true
          estimatedQuantityRef.current = totalSeats
          setLatestPlans(plansData)
          const unit = selectedUnitRef.current
          const plan = plansData.find((p) => p.unit === unit)
          setEstimate(plan?.estimate ?? null)
          setEstimateLoading(false)
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            // A failure clears the estimate, so the quantity it was priced for is no longer held.
            estimatedQuantityRef.current = null
            setEstimate(null)
            setEstimateLoading(false)
          }
        })
    }, 500)
    return () => {
      clearTimeout(debounceRef.current)
      controller.abort()
    }
  }, [totalSeats, selectedUnit, open, subscription])

  const displayPlans = latestPlans.filter((p) => !p.comparisonOnly)
  const selectedPlanData = latestPlans.find((p) => p.unit === selectedUnit)
  const currentTier = PLAN_TIER[subscription?.planLicense?.unit] ?? -1
  const selectedTier = PLAN_TIER[selectedUnit] ?? -1
  const isUpgrade = selectedTier > currentTier

  const planStat = subscription?.planStats?.[0]
  const unusedSeats = (planStat?.totalSeats ?? 0) - (planStat?.filledSeats ?? 0)

  const handlePaymentMethodChange = async (paymentId) => {
    const prevMethods = paymentMethods
    const prevSelectedId = selectedPaymentId
    setSelectedPaymentId(paymentId)
    setPaymentMethods((prev) =>
      prev.map((m) => ({ ...m, primary: m.id?.toString() === paymentId }))
    )
    try {
      await updatePrimaryPaymentMethod(company.id, paymentId, jwt, csrf)
      toast.success('Successfully saved the payment method as default', undefined, { showCloseButton: false })
    } catch (error) {
      toast.critical(error.message || 'Failed to update the payment method as default', undefined, { showCloseButton: false })
      setSelectedPaymentId(prevSelectedId)
      setPaymentMethods(prevMethods)
    }
  }

  const handleProceed = async () => {
    if (!selectedPaymentId) {
      toast.critical('Please add a payment method before proceeding', undefined, { showCloseButton: false })
      return
    }
    setIsSubmitting(true)
    try {
      const knownInlineSubscription = variant === 'inline' ? pendingInlineSubscription : null

      let updatedSubscription
      if (knownInlineSubscription) {
        const unchanged = selectedUnit === knownInlineSubscription.unit && totalSeats === knownInlineSubscription.totalSeats
        if (unchanged) {
          updatedSubscription = { id: knownInlineSubscription.id }
        } else {
          const isOnlySeatUpdate = selectedUnit === knownInlineSubscription.unit
          const mode = isOnlySeatUpdate ? 'update_quantity_payment' : null
          updatedSubscription = await updateSubscription(company.id, knownInlineSubscription.id, jwt, csrf, selectedUnit, totalSeats, mode, { announcementRenderToken })
        }
      } else if (isNewOrCanceledSubscription) {
        updatedSubscription = await createSubscription(company.id, jwt, csrf, selectedUnit, seats, { announcementRenderToken })
      } else {
        const isOnlySeatUpdate = selectedUnit === subscription.planLicense?.unit
        const mode = isOnlySeatUpdate ? 'update_quantity_payment' : null
        updatedSubscription = await updateSubscription(company.id, subscription.id, jwt, csrf, selectedUnit, totalSeats, mode, { announcementRenderToken })
      }

      if (variant === 'inline') {
        onInlineSubscriptionChange?.({ id: updatedSubscription.id, unit: selectedUnit, totalSeats })
        const url = await fetchQuickbooksCallbackUrl(company.id, updatedSubscription.id, jwt)
        closeQuickbooksIframe(url)
        return
      }

      Promise.resolve(refreshSubscription?.()).catch(() => { })
      setSuccessPlanName(selectedPlanData?.name ?? '')
      setSuccessIsUpgrade(isUpgrade)
      setIsSuccess(true)
      onSuccess?.()
    } catch (error) {
      toast.critical(error.message || 'Failed to update subscription', undefined, { showCloseButton: false })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Closing the success step (via "Awesome!", the X, or the overlay) should send the
  // user back to the Course Authoring page and re-open the Create Course modal when the
  // upgrade started from that flow.
  const handleDialogOpenChange = (nextOpen) => {
    if (!nextOpen && isSuccess && source === UPGRADE_SOURCE_COURSE_AUTHORING && company?.id) {
      onOpenChange(false)
      window.location.assign(`${courseAuthoringTrainingsPath(company.id)}?${REOPEN_CREATE_COURSE_PARAM}=true`)
      return
    }
    onOpenChange(nextOpen)
  }

  const handleAddPaymentSuccess = async () => {
    setAddPaymentOpen(false)
    try {
      await refreshPaymentMethods()
      toast.success('Payment method added successfully', undefined, { showCloseButton: false })
    } catch {
      toast.critical('Failed to refresh payment methods', undefined, { showCloseButton: false })
    }
  }

  const isCurrentPlan = selectedUnit === subscription?.planLicense?.unit

  const shouldDisableProceedButton =
    isSubmitting ||
    !selectedUnit ||
    !estimate ||
    estimateLoading ||
    (seats === 0 && isCurrentPlan)

  const panelsClassName = variant === 'inline'
    ? 'flex divide-x divide-neutral-200 max-lg:flex-col max-lg:divide-x-0'
    : 'flex divide-x divide-neutral-200 max-lg:max-h-[65vh] max-lg:overflow-hidden'

  const panels = (
    <>
      <div className={panelsClassName}>
        <ChangePlanFormPanel
          subscription={subscription}
          unusedSeats={variant === 'inline' ? 0 : unusedSeats}
          seats={seats}
          onSeatsChange={setSeats}
          isCounterDisabled={estimateLoading}
          plans={displayPlans}
          selectedUnit={selectedUnit}
          onPlanSelect={setSelectedUnit}
          restrictUnit={restrictUnit}
          paymentMethods={paymentMethods}
          selectedPaymentId={selectedPaymentId}
          onPaymentChange={handlePaymentMethodChange}
          onAddPayment={() => setAddPaymentOpen(true)}
          estimate={estimate}
          onViewDetails={onViewDetails ? onViewDetails : () => onOpenChange(false)}
          termsClassName={variant === 'inline' ? 'hidden lg:block mt-7' : 'mt-7'}
        />
        <ChangePlanSummaryPanel
          selectedPlanData={selectedPlanData}
          seats={totalSeats}
          estimate={estimate}
          estimateLoading={estimateLoading}
          subscription={subscription}
          onContactSales={() => setSalesDialogMode('contact')}
          showContactSales={showContactSales}
          simplified={variant === 'inline'}
          className={variant === 'inline' ? 'max-lg:w-full' : ''}
        />
      </div>
      {variant === 'inline' && (
        <div className='lg:hidden px-6 pb-4'>
          <ChangePlanTerms estimate={estimate} totalSeats={totalSeats} cadence={subscription?.cadence} />
        </div>
      )}
    </>
  )

  const footerButtons = (
    <>
      <AppButton
        variant='secondary'
        onClick={() => onOpenChange(false)}
        className='max-sm:w-full'
      >
        Cancel
      </AppButton>
      <AppButton
        onClick={handleProceed}
        disabled={shouldDisableProceedButton}
        className='disabled:cursor-not-allowed max-sm:w-full'
      >
        Proceed and pay
      </AppButton>
    </>
  )

  const addPaymentAndContactSalesDialogs = (
    <>
      <AddPaymentMethodDialog
        open={open && addPaymentOpen}
        handleOpenChange={(val) => { if (!val) setAddPaymentOpen(false) }}
        onSuccess={handleAddPaymentSuccess}
        onBack={variant === 'inline' ? undefined : () => setAddPaymentOpen(false)}
      />

      {showContactSales && (
        <ContactSalesDialog
          open={salesDialogMode !== null}
          handleOpenChange={(val) => { if (!val) setSalesDialogMode(null) }}
          dialogTitle='Contact sales'
          title='Have questions?'
          description={''}
        />
      )}
    </>
  )

  if (variant === 'inline') {
    if (!open) return addPaymentAndContactSalesDialogs

    return (
      <>
        <div className='max-lg:flex-1 max-lg:min-h-0 flex flex-col lining-nums border border-[#d4d4d4] rounded-xl overflow-hidden max-sm:border-0 max-sm:rounded-none'>
          <div className='px-6 py-4 max-sm:pt-4 max-sm:pb-3 border-b border-neutral-200 shrink-0'>
            <h2 className='text-base font-bold text-llama-grey-7'>Confirm and Purchase Your Plan</h2>
          </div>
          <div className='max-lg:flex-1 max-lg:min-h-0 max-lg:overflow-y-auto'>
            {panels}
          </div>
          <div className='flex justify-end gap-3 px-6 py-4 border-t border-neutral-200 max-sm:flex-col-reverse shrink-0'>
            {footerButtons}
          </div>
        </div>

        {addPaymentAndContactSalesDialogs}
      </>
    )
  }

  return (
    <>
      <AppDialog open={open && !addPaymentOpen} onOpenChange={handleDialogOpenChange}>
        <AppDialogContent size={isSuccess ? 'sm' : 'lg'} style={isSuccess ? { maxWidth: '286px', width: '286px' } : undefined}>
          {isSuccess ? (
            <AppDialogHeader>
              <AppDialogTitle className='sr-only'>Plan changed</AppDialogTitle>
              <AppDialogDescription className='sr-only'>Your plan has been updated.</AppDialogDescription>
            </AppDialogHeader>
          ) : (
            <AppDialogHeader>
              <AppDialogTitle>Upgrade plan</AppDialogTitle>
              <AppDialogDescription className='el:sr-only'>
                Review and confirm your plan change
              </AppDialogDescription>
            </AppDialogHeader>
          )}

          {isSuccess ? (
            <ChangePlanSuccessStep
              onClose={() => handleDialogOpenChange(false)}
            />
          ) : (
            <>
              {/* data-safari-p-0: without it the blanket dialog-body rule puts padding back on Safari,
                  undoing this !p-0. Same marker PlansDialog uses. */}
              <AppDialogBody className='lining-nums !p-0 max-lg:overflow-hidden !border-b-0 border-t border-neutral-200' data-safari-p-0>
                {panels}
              </AppDialogBody>

              <AppDialogFooter className='!border-t-0 max-sm:!flex-col-reverse'>
                {footerButtons}
              </AppDialogFooter>
            </>
          )}
        </AppDialogContent>
      </AppDialog>

      {addPaymentAndContactSalesDialogs}
    </>
  )
}
